/**
 * Unida (Empresa Unida) Hotjar satisfaction survey targeting.
 *
 * Complete puts `beloHorizonte=true` on the URL when a completed purchase starts or ends in
 * Belo Horizonte. Hotjar itself is loaded by GTM, not by the funnel, and the survey does not
 * depend on cookie consent — this module only decides the URL flag.
 *
 * Unida Places uses `belo-horizonte-mg` / `BELO HORIZONTE - MG`, not the bare `belo-horizonte` slug.
 */
export const EMPRESA_UNIDA_BRAND = 'empresaunida';
export const BELO_HORIZONTE_CITY_ID = 'belo-horizonte';
export const BELO_HORIZONTE_CITY_NAME = 'belo horizonte';

/**
 * Normalize a Places cityId so `belo_horizonte` and `Belo-Horizonte` match the canonical slug.
 * @param {string} [cityId] - Places city identifier.
 * @returns {string} Lowercased slug with underscores turned into hyphens.
 */
export function normalizeCityId(cityId) {
  if (!cityId || typeof cityId !== 'string') return '';
  return cityId.trim().toLowerCase().replace(/_/g, '-');
}

/**
 * Normalize a city display name for equality checks.
 * @param {string} [cityName] - Human-readable city name.
 * @returns {string} Trimmed lowercase name.
 */
export function normalizeCityName(cityName) {
  if (!cityName || typeof cityName !== 'string') return '';
  return cityName.trim().toLowerCase();
}

/**
 * Whether a cityId is Belo Horizonte (`belo-horizonte` or `belo-horizonte-mg`).
 * @param {string} [cityId] - Places city identifier.
 * @returns {boolean} True when the slug is Belo Horizonte.
 */
export function isBeloHorizonteCityId(cityId) {
  const id = normalizeCityId(cityId);
  return id === BELO_HORIZONTE_CITY_ID || id.startsWith(`${BELO_HORIZONTE_CITY_ID}-`);
}

/**
 * Whether a city display name is Belo Horizonte (`Belo Horizonte` or `BELO HORIZONTE - MG`).
 * @param {string} [cityName] - Human-readable city name.
 * @returns {boolean} True when the name is Belo Horizonte.
 */
export function isBeloHorizonteCityName(cityName) {
  const name = normalizeCityName(cityName);
  return name === BELO_HORIZONTE_CITY_NAME || name.startsWith(`${BELO_HORIZONTE_CITY_NAME} `);
}

/**
 * Whether a stop (terminal/city) is Belo Horizonte.
 * @param {object} [place] - Origin or destination with `cityId` and/or `cityName`.
 * @returns {boolean} True when the place is Belo Horizonte.
 */
export function placeIsBeloHorizonte(place) {
  if (!place) return false;
  return isBeloHorizonteCityId(place.cityId) || isBeloHorizonteCityName(place.cityName);
}

/**
 * Route origin and destination for a way, ignoring intermediate connection stops.
 * @param {object} [way] - Serialized `departs` or `returns` purchase way.
 * @returns {Array<object|undefined>} `[origin, destination]` of the route.
 */
export function wayRoutePlaces(way) {
  if (!way) return [];
  const fragments = way.fragments || [];
  const origin = way.origin || fragments[0]?.origin;
  const destination = way.destination || fragments[fragments.length - 1]?.destination;
  return [origin, destination];
}

/**
 * Whether the purchase route (outbound or return) starts or ends in Belo Horizonte.
 * Intermediate connection stops do not count: satisfaction targeting is origin-or-destination.
 * @param {object} purchase - Serialized purchase (departs/returns ways).
 * @returns {boolean} True when origin or destination of departs or returns is Belo Horizonte.
 */
export function purchaseTouchesBeloHorizonte(purchase) {
  if (!purchase) return false;
  const ways = [purchase.departs, purchase.returns];
  return ways.some((way) => wayRoutePlaces(way).some(placeIsBeloHorizonte));
}

/**
 * Whether the Unida Hotjar URL flag should be exposed.
 * Unlike the Doters flag, a purchase still settling does not qualify: Unida's Pix lands on
 * Complete as `attempt` with the QR still unpaid, and an NPS answered there may belong to a
 * purchase that never completes.
 * @param {object} params - Brand, purchase, and purchase state.
 * @param {string} [params.brand] - Whitelabel brand key (`env.brand`).
 * @param {object} [params.purchase] - Serialized purchase.
 * @param {string} [params.purchaseStatus] - Purchase status; only `completed` qualifies.
 * @param {boolean} [params.failed=false] - True when the payment failed/canceled/expired.
 * @returns {boolean} True for completed Empresa Unida purchases whose route starts or ends in Belo Horizonte.
 */
export function shouldFlagUnidaBeloHorizonte({
  brand,
  purchase,
  purchaseStatus,
  failed = false,
} = {}) {
  return (
    brand === EMPRESA_UNIDA_BRAND &&
    purchaseStatus === 'completed' &&
    !failed &&
    purchaseTouchesBeloHorizonte(purchase)
  );
}
